import React, { useEffect, useState } from 'react';
import { useModalLayer } from '../../lib/modalStack';
import { Building2, ChevronDown, Landmark, Users } from 'lucide-react';
import type { OrgTeamRole } from '../../lib/teamSelection';
import { useTeamSelection } from './TeamSelectionGate';
import { TEAM_ROLE_LABEL } from './TeamSelectionScreen';

/**
 * TeamSwitcher — the shared in-dashboard team switcher (Slice 3,
 * plan §(d)). One component in all three organization dashboards
 * (Referrer / Sponsor / Employer), replacing the employer header's
 * bespoke company dropdown and giving referrer + sponsor the same
 * affordance.
 *
 * TERMINOLOGY (owner ruling, Oct 15 2026): a "team" here is an
 * ORGANIZATION team (Referrer Team / Sponsor Team / Employer Team),
 * never the workspace staff team — the menu is therefore labeled
 * "Switch Team" in every dashboard (the retired employer dropdown
 * said "Switch Workspace", which conflated the two).
 *
 * The switcher is a client affordance over the gate's resolution, not
 * a security mechanism: it renders the eligible list the
 * TeamSelectionGate fetched from `GET /api/{role}/teams` (the Slice 1
 * enumeration — one list, two consumers) and switches through the
 * gate, which persists the session selection and remounts the
 * dashboard subtree under the new team key. Switching changes only
 * the ACTIVE org context: never identity, role, workspace (D-8), or
 * any membership. The server re-verifies the selection on every
 * request regardless.
 *
 * Render contract:
 *  - gate not engaged / zero eligible teams (passthrough) ⇒ the
 *    `fallbackName` (the dashboard's own legacy name source) or
 *    nothing — legacy headers are byte-identical;
 *  - exactly one eligible team ⇒ the team name as a static label —
 *    context is never implicit, and there is no dead control;
 *  - more than one ⇒ the name plus a menu of all eligible teams with
 *    rank badges and an "Active" marker on the current one.
 */

const ROLE_ICON: Record<OrgTeamRole, typeof Building2> = {
  employer: Building2,
  referrer: Users,
  sponsor: Landmark,
};

function RankChip({ rank }: { rank: 'super' | 'member' }) {
  return (
    <span
      className={
        rank === 'super'
          ? 'text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0'
          : 'text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 border border-slate-200 shrink-0'
      }
    >
      {rank === 'super' ? 'Super Agent' : 'Team Member'}
    </span>
  );
}

interface TeamSwitcherProps {
  role: OrgTeamRole;
  /** Name shown when the gate is not engaged (zero-team passthrough / standalone mount). */
  fallbackName?: string;
  /** Classes for the active team's name (trigger + static label). */
  nameClassName?: string;
  /** Classes for the fallback name; defaults to nameClassName. */
  fallbackClassName?: string;
  /** Render the name inside an <h1> (the employer header's heading slot). */
  nameElement?: 'span' | 'h1';
  /** Fired with the newly chosen team id just before the gate switches. */
  onSwitch?: (teamId: string) => void;
}

export default function TeamSwitcher({
  role,
  fallbackName,
  nameClassName = 'text-sm font-black text-slate-900 truncate',
  fallbackClassName,
  nameElement = 'span',
  onSwitch,
}: TeamSwitcherProps) {
  const ctx = useTeamSelection();
  const [open, setOpen] = useState(false);
  // SYS-11: layer assigned by the modal stack while open (the z scale
  // has no generated named utilities in Tailwind v4 — overlays register
  // with the stack instead; see index.css).
  const layerZ = useModalLayer(open);

  const activeTeamId = ctx && ctx.role === role ? ctx.activeTeamId : null;
  const teams = ctx && ctx.role === role ? ctx.teams : [];
  const activeTeam = activeTeamId ? teams.find((t) => t.id === activeTeamId) : undefined;

  // A switch (ours, the picker's, or a recovery re-resolution) closes
  // any open menu — the list it showed belongs to the old context.
  useEffect(() => { setOpen(false); }, [activeTeamId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open ]);

  const NameTag = nameElement;

  if (!activeTeam) {
    // Gate not engaged, zero eligible teams, or the active id is not
    // in the resolved list — show the dashboard's own name source.
    if (!fallbackName) return null;
    return <NameTag className={fallbackClassName ?? nameClassName}>{fallbackName}</NameTag>;
  }

  if (teams.length === 1) {
    // Single team: static labeled context, no dead control.
    return (
      <NameTag className={nameClassName} title={activeTeam.name}>
        {activeTeam.name}
      </NameTag>
    );
  }

  const Icon = ROLE_ICON[role];
  const label = TEAM_ROLE_LABEL[role];

  return (
    <div className="relative flex items-center min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Switch ${label} team — current team: ${activeTeam.name}`}
        className="flex items-center gap-1.5 min-w-0 px-1.5 py-1 -ml-1.5 rounded-lg hover:bg-slate-100 transition cursor-pointer"
      >
        <NameTag className={nameClassName} title={activeTeam.name}>
          {activeTeam.name}
        </NameTag>
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <>
          <div className="fixed inset-0" style={{ zIndex: layerZ }} onClick={() => setOpen(false)} />
          <div
            role="listbox"
            aria-label="Switch team"
            className="absolute left-0 top-full mt-1 w-64 max-w-[calc(100vw-2rem)] max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl py-1"
            style={{ zIndex: layerZ }}
          >
            <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wide">
              Switch Team
            </div>
            {teams.map((team) => {
              const isActive = team.id === activeTeam.id;
              const restricted = role === 'referrer' && team.status.toLowerCase() === 'deactivated';
              return (
                <button
                  key={team.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    setOpen(false);
                    if (!isActive) {
                      onSwitch?.(team.id);
                      ctx?.switchTeam(team.id);
                    }
                  }}
                  className={`w-full text-left px-3 py-2 text-xs font-medium flex items-start gap-2 transition cursor-pointer ${
                    isActive ? 'bg-brand-active' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="truncate" title={team.name}>
                        {team.name || `${label} Team`}
                      </span>
                      {isActive && (
                        <span className="ml-auto text-[10px] text-brand font-bold shrink-0">Active</span>
                      )}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1">
                      <RankChip rank={team.role} />
                      {restricted && (
                        <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                          Restricted — read-only
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
